// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import Icon from "@/UI/atoms/Icon";
import PasswordInput from "@/UI/inputs/PasswordInput";
// --- Types --------------------------------------------------------------------------------------
export type TyPasswordFormDetails = { onDone: () => void };

type Props = { onClose: () => void; details?: unknown };
// --- Logic --------------------------------------------------------------------------------------
/**
 * Changing one's **own** password — the only password action anybody in this product may perform on
 * themselves. Issuing somebody else's is a different thing entirely and lives on their row.
 *
 * It is the app's first form, so it is the template: the `side-form` shell from `PopupPaper`, centred
 * because three fields in a full-height panel is mostly empty space, the fields' own validation before
 * the request, and the API's refusal shown where the person is already looking.
 *
 * The confirmation field is ours, not the API's — the backend has no use for it, and a typo in a
 * password nobody can read back is the one mistake this form exists to prevent.
 */
export default function PasswordFormPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { onDone } = (details ?? {}) as TyPasswordFormDetails;

    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [formError, setFormError] = useState<string | null>(null);

    const { loading, error, succeededAt, invoke } = useFetch("/auth/password", { method: "PATCH" }, false);

    useEffect(() => {
        if (!succeededAt) return;
        onDone?.();
        onClose();
    }, [succeededAt, onDone, onClose]);

    const handleSubmit = () => {
        setFormError(null);
        if (!currentPassword) return setFormError(t.auth.passwordRequired);
        if (newPassword.length < constants.minPasswordLength) {
            return setFormError(t.profile.passwordTooShort.replace("{count}", String(constants.minPasswordLength)));
        }
        if (newPassword !== confirmPassword) return setFormError(t.profile.passwordMismatch);

        invoke({ method: "PATCH", data: { currentPassword, newPassword } });
    };

    return (
        <section className="side-form side-form--center">
            <header className="side-form__header">
                <h2 className="side-form__title">{t.profile.changePassword}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                <PasswordInput
                    value={currentPassword}
                    onChange={setCurrentPassword}
                    label={t.profile.currentPassword}
                    disabled={loading}
                    onEnter={handleSubmit}
                />
                <PasswordInput
                    value={newPassword}
                    onChange={setNewPassword}
                    label={t.profile.newPassword}
                    disabled={loading}
                    onEnter={handleSubmit}
                />
                <PasswordInput
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                    label={t.profile.confirmPassword}
                    disabled={loading}
                    onEnter={handleSubmit}
                />

                <p className="side-form__hint">{t.profile.passwordHint}</p>
                {(formError || error) && <p className="side-form__error">{formError ?? error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={loading}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit} disabled={loading}>
                    {loading ? t.common.loading : t.common.save}
                </button>
            </footer>
        </section>
    );
}
